class ProjectionDemo {
    constructor(debug) {
        console.log('projectorDemo loaded 📽️');
        this.debug = debug || false;
        this.processAllCanvases();
    };

    // container for all scenes that have been loaded before (so we don't have to load them everytime)
    scenes = {};
    // container for all gobos that have been loaded before (so we don't have to load them everytime)
    allGobos = {};
    // all canvases
    allCanvas = {};
    // current gobo
    gobo = null;
    // current scene
    scene = null;
    // current canvas
    canvas = null;
    // render queue
    queue = [];
    // start queue time
    startQueue = null;
    // wait slot
    currentLoop = null;
    // debug flag
    debug = false;
    // error counter
    errorCount = {};

    startInterval() {
        this.startQueue = Date.now();
        this.currentLoop = setInterval(async () => {
            if (this.queue.length > 0) {
                const nextUp = this.queue.pop();
                if (this.debug) {
                    console.log('Attempting render of', nextUp);
                }
                await this.setupAndRender(nextUp);
            } else {
                this.stopInterval();
                if (this.debug) {
                    console.log('Render queue finished.', this.currentLoop);
                }
            }
        }, 50);
    };

    stopInterval() {
        if (this.queue.length > 0) {
            console.warn('More renders in the queue. Not stopping renderer...');
            return;
        }
        clearInterval(this.currentLoop);
        this.currentLoop = null;
        console.log(`Render queue cleared in ${(Date.now() - this.startQueue) / 1000}s`);
    }

    processAllCanvases() {
        console.log('Processing canvases');
        this.allCanvas = {};
        const canvasArray = [...document.getElementsByClassName('gobo-projection')];
        canvasArray.forEach((e) => {
            if (e.dataset.product) {
                this.parseCanvas(e);
            }
        });
    };

    async parseCanvas(canvas) {
        const id = canvas.dataset.product;
        const image = canvas.dataset.image;
        const scene = canvas.dataset.scene;

        if (this.allCanvas[id]) {
            console.log(id, 'Canvas already processed.');
            return;
        }

        this.allCanvas[id] = canvas;
        this.addGobo(id, image);
        this.addScene(id, scene);
        await this.setupAndRender(id);
    }

    setCanvas(id) {
        this.canvas = this.allCanvas[id];
    };

    addScene(id, url) {
        if (!url) {
            throw new Error('No URL passed to addScene');
        }
        const sceneName = url.split('/').pop();
        if (!this.scenes[sceneName]) {
            this.scenes[sceneName] = new Image();
            this.scenes[sceneName].crossOrigin = 'anonymous';
            this.scenes[sceneName].src = url;
            this.scene = this.scenes[sceneName];
            this.allGobos[id].scene = sceneName;
        } else {
            this.allGobos[id].scene = sceneName;
        }
    }

    addGobo(id, url) {
        if (this.allGobos[id]) {
            this.gobo = this.allGobos[id];
        } else {
            if (!url) {
                throw new Error('No URL provided to gobo loader');
            }
            this.allGobos[id] = {
                image: null,
                scene: null
            };
            this.allGobos[id].image = new Image();
            this.allGobos[id].image.crossOrigin = 'anonymous';
            this.allGobos[id].image.src = url;
            this.gobo = this.allGobos[id];
        }
    };

    async renderProjection() {
        const start = Date.now();
        const { canvas, gobo, debug } = this;
        const ctx = this.canvas.getContext('2d');

        function renderGobo(translationMatrix) {
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');

            tempCanvas.width = canvas.width;
            tempCanvas.height = canvas.height;

            tempCtx.drawImage(
                gobo.image, 0, 0, canvas.width, canvas.height
            );

            const blurRadius = 0.5;
            tempCtx.filter = 'blur(' + blurRadius + 'px)';
            tempCtx.drawImage(tempCanvas, 0, 0);

            const fxCanvas = fx.canvas();
            const texture = fxCanvas.texture(tempCanvas);

            fxCanvas
                .draw(texture)
                .perspective(
                    [0, 0, 1024, 0, 1024, 1024, 0, 1024],
                    translationMatrix || [0, 0, 1024, 0, 1024, 1024, 0, 1024]
                )
                .lensBlur(3, 0.1, 0)
                .noise(0.1)
                .update();

            ctx.globalAlpha = 0.75;
            if (!debug) {
                ctx.globalCompositeOperation = 'screen';
            }
            ctx.drawImage(fxCanvas, 0, 0);
        };

        function drawGrid(spacing, colour) {
            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'normal';
            function drawLine(x, y) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, canvas.height);
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            };

            if (typeof spacing !== 'number') {
                throw new Error('drawGrid not passed a number.');
            }
            // Set grid line color (optional)
            ctx.strokeStyle = colour || 'black';

            // Draw vertical lines
            for (let i = 0; i <= Math.round(canvas.width / spacing); i++) {
                drawLine(i * spacing, 0);
            }

            // Draw horizontal lines
            for (let i = 0; i <= Math.round(canvas.height / spacing); i++) {
                drawLine(0, i * spacing);
            }
        };

        if (canvas.dataset.rendered === 'true') {
            console.log(canvas.dataset.product, 'already rendered. Skipping...');
            return;
        }

        // early exits
        if (!this.scene.complete) {
            console.warn('renderProjection: Scene image not loaded. Added to render queue.');
            this.queue.push(canvas.dataset.product);
            if (!this.currentLoop) {
                this.startInterval();
            }
            return;
        }
        if (!this.gobo.image.complete) {
            console.warn('renderProjection: Gobo image not loaded. Added to render queue.');
            this.queue.push(canvas.dataset.product);
            if (!this.currentLoop) {
                this.startInterval();
            }
            return;
        }

        // render main image
        try {
            try {
                await this.scene.decode();
            } catch (error) {
                const newURL = `${window.shopUrl}/cdn/${this.scene.src.split('/cdn/')[1]}`;
                this.scenes[this.gobo.scene] = new Image();
                this.scenes[this.gobo.scene].crossOrigin = 'anonymous';
                this.scenes[this.gobo.scene].src = newURL;
                throw new Error(`Failed to decode scene image for ${canvas.dataset.product}: ${this.scene.src}. Changing src...`);
            }
            ctx.drawImage(this.scene, 0, 0, canvas.width, canvas.height);
        } catch (error) {
            console.error('Error rendering', canvas.dataset.product, 'Readding to queue.');
            // console.error(error);
            if (this.errorCount[canvas.dataset.product]) {
                this.errorCount[canvas.dataset.product]++;
            } else {
                this.errorCount[canvas.dataset.product] = 1;
            }
            if (this.errorCount[canvas.dataset.product] <= 2) {
                this.queue.push(canvas.dataset.product);
                return;
            } else {
                console.warn('Max retry limit reached for', canvas.dataset.product);
                console.log(this.scene.src.split('/'));
                return;
            }
        }

        // render Gobo
        try {
            try {
                await gobo.image.decode();
            } catch (error) {
                const newURL = `${window.shopUrl}/cdn/${gobo.image.src.split('/cdn/')[1]}`;
                this.allGobos[canvas.dataset.product].image = new Image();
                this.allGobos[canvas.dataset.product].image.crossOrigin = 'anonymous';
                this.allGobos[canvas.dataset.product].image.src = newURL;
                throw new Error(`Failed to decode gobo image for ${canvas.dataset.product}: ${gobo.image.src}. Changing src...`);
            }
            renderGobo([
                // top left
                100, 0,
                // top right
                785, 220,
                // bottom right
                785, 850,
                // bottom left
                100, 1050
            ]);
        } catch (error) {
            console.error('Error rendering', canvas.dataset.product, 'Readding to queue.');
            console.error(error);
            if (this.errorCount[canvas.dataset.product]) {
                this.errorCount[canvas.dataset.product]++;
            } else {
                this.errorCount[canvas.dataset.product] = 1;
            }
            if (this.errorCount[canvas.dataset.product] <= 2) {
                this.queue.push(canvas.dataset.product);
                return;
            } else {
                console.warn('Max retry limit reached for', canvas.dataset.product);
                console.log(this.gobo.image.src.split('/'));
                return;
            }
        }

        // draw grid
        // drawGrid(25, 'red');
        if (debug) {
            console.log(`Render of ${canvas.dataset.product} took ${(Date.now() - start) / 1000}s`);
        }
        canvas.dataset.rendered = true;
        // console.log(canvas.parentElement.children);
        const loadingSymbol = [...canvas.parentElement.children].find((e) => e?.classList?.contains('gobo-loading'));
        if (loadingSymbol) {
            console.log(canvas.dataset.product, 'rendered successfully.');
            loadingSymbol.remove();
        }
    };

    async setupAndRender(id) {
        if (!this.allGobos[id]) {
            throw new Error('Gobo not loaded.');
        }
        this.canvas = this.allCanvas[id];
        // console.log(this.canvas);
        this.gobo = this.allGobos[id];
        // console.log(this.gobo);
        this.scene = this.scenes[this.gobo.scene];
        // console.log(this.scene);
        await this.renderProjection();
    }
}

if (document.readyState === "complete" || document.readyState === "loaded") {
    // console.log('Document was already loaded');
    if (!window.projectionDemo) {
        // init
        window.projectionDemo = new ProjectionDemo();
        console.log('projectorDemo loaded 📽️');
    } else {
        console.warn('ProjectionDemo already loaded');
    }
} else {
    document.addEventListener('DOMContentLoaded', () => {
        // console.log('DOMContentLoaded');
        if (!window.projectionDemo) {
            // init
            window.projectionDemo = new ProjectionDemo();
        } else {
            console.warn('ProjectionDemo already loaded');
        }
    });
}